import React from 'react';
import {
  PreviewTabProps, SectionLabel, ActionButton, TextArea,
  abilityRegistry, eventBus,
} from './shared';
import { livePlayerPosition } from '../../../../systems/player/livePlayerPosition';

export const PreviewTab: React.FC<PreviewTabProps> = ({ ability, update, onCastAbility, importJson, setImportJson, player, getVfxId }) => {
  const playFullAbility = () => onCastAbility?.(ability.id);

  const playVfxOnly = () => {
    if (!player) return;
    eventBus.emit('vfx:spawn', {
      effectId: getVfxId(),
      x: livePlayerPosition(player?.position).x + (player.facing === 'right' ? 50 : -50),
      y: livePlayerPosition(player?.position).y - 10,
      color: ability.vfx?.color,
      scale: ability.vfx?.scale,
      duration: ability.vfx?.duration,
    });
  };

  const playDamageNumber = () => {
    if (!player) return;
    const rawDmg = ability.projectile?.damage ?? (ability as any).damageFormula;
    const dmg = typeof rawDmg === 'number' ? rawDmg : (typeof rawDmg === 'string' && rawDmg !== 'Scaling' && rawDmg !== 'Dynamic' ? parseInt(rawDmg) || 25 : 25);
    eventBus.emit('vfx:damage_number', {
      x: livePlayerPosition(player?.position).x + 30,
      y: livePlayerPosition(player?.position).y - 30,
      amount: dmg,
      type: 'damage',
      critical: false,
    });
    setTimeout(() => {
      eventBus.emit('vfx:damage_number', {
        x: livePlayerPosition(player?.position).x + 50,
        y: livePlayerPosition(player?.position).y - 40,
        amount: Math.floor(dmg * 1.5),
        type: 'damage',
        critical: true,
      });
    }, 500);
  };

  const exportConfig = () => {
    const config = {
      id: ability.id,
      name: ability.name,
      description: ability.description,
      tags: ability.tags,
      manaCost: ability.manaCost,
      cooldown: ability.cooldown,
      castTime: ability.castTime,
      icon: ability.icon,
      projectile: ability.projectile,
      vfx: ability.vfx,
    };
    const json = JSON.stringify(config, null, 2);
    navigator.clipboard.writeText(json).catch(() => {});
    setImportJson(json);
  };

  const importConfig = () => {
    try {
      const parsed = JSON.parse(importJson);
      const { id: _id, name: _name, fn: _fn, ...patch } = parsed;
      update(patch);
    } catch { /* invalid JSON — ignore */ }
  };

  return (
    <>
      <SectionLabel>Quick Preview</SectionLabel>
      {onCastAbility && (
        <ActionButton $variant="green" onClick={playFullAbility}>
          ▶ Play Full Ability
        </ActionButton>
      )}
      <ActionButton $variant="yellow" onClick={playVfxOnly}>
        ✨ Play VFX Only
      </ActionButton>
      <ActionButton $variant="blue" onClick={playDamageNumber}>
        💥 Show Damage Numbers
      </ActionButton>

      <SectionLabel>Shape Previews</SectionLabel>
      {ability.melee && (
        <ActionButton $variant="yellow" onClick={() => {
          if (!player) return;
          eventBus.emit('vfx:melee_swing', {
            x: livePlayerPosition(player?.position).x, y: livePlayerPosition(player?.position).y,
            isRight: player.facing === 'right',
            color: ability.melee?.lineColor ?? 0xFF4444,
            arcAngle: ability.melee?.arcAngle ?? -45,
            arcSpread: ability.melee?.arcSpread ?? 90,
            arcRadius: ability.melee?.arcRadius ?? 60,
            lineWidth: ability.melee?.lineWidth ?? 4,
            duration: ability.melee?.swingDuration ?? 150,
          });
        }}>
          ⚔ Preview Melee Arc
        </ActionButton>
      )}
      <ActionButton $variant="yellow" onClick={() => {
        if (!player) return;
        const range = (ability as any).range ?? 400;
        const isRight = player.facing === 'right';
        eventBus.emit('vfx:hitscan_beam', {
          startX: livePlayerPosition(player?.position).x, startY: livePlayerPosition(player?.position).y,
          endX: livePlayerPosition(player?.position).x + (isRight ? range : -range), endY: livePlayerPosition(player?.position).y,
          color: ability.projectile?.color ?? 0xFFFFFF, width: 3, duration: 300,
        });
      }}>
        ⚡ Preview Hitscan Beam
      </ActionButton>
      <ActionButton $variant="yellow" onClick={() => {
        if (!player) return;
        eventBus.emit('vfx:spawn', {
          effectId: 'procedural_ring_expand',
          x: livePlayerPosition(player?.position).x, y: livePlayerPosition(player?.position).y,
        });
      }}>
        💫 Preview Nova Burst
      </ActionButton>

      <SectionLabel>Export / Import</SectionLabel>
      <ActionButton $variant="green" onClick={exportConfig}>
        📋 Export JSON to Clipboard
      </ActionButton>
      <TextArea
        value={importJson}
        onChange={e => setImportJson(e.target.value)}
        placeholder='Paste ability JSON here...'
      />
      <ActionButton $variant="blue" onClick={importConfig}>
        📥 Import JSON Config
      </ActionButton>

      <SectionLabel>Reset</SectionLabel>
      <ActionButton $variant="red" onClick={() => {
        // Re-register the original ability function to reset metadata
        const fn = ability.fn;
        const originalMeta = (fn as any).metadata;
        if (originalMeta) {
          abilityRegistry.updateMetadata(ability.id, originalMeta);
        }
      }}>
        🔄 Reset to Defaults
      </ActionButton>
    </>
  );
};
